import React, { useMemo, useState } from 'react';
import { Box, Button, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Add, Delete, Edit, Storefront } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuthHeaders, makeApiCall } from '../../utils/apiConfig';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useToast } from '../../contexts/ToastContext';
import { useLocale } from '../../contexts/LocaleContext';
import { tokens } from '../../theme/tokens';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import {
  ChipInput,
  ConfirmDialog,
  EmptyState,
  FormDialog,
  InlineMessage,
  NumberField,
  ResponsiveRows,
  SectionCard,
  StatusChip,
  parseNumberInput,
} from '../ui';
import type { StatusTone } from '../ui';
import type { CrosswalkCondition } from './SkuCrosswalkPanel';

type UnitStatus = 'draft' | 'listed' | 'sold' | 'removed';
type UnitGrade = 'like_new' | 'excellent' | 'good' | 'fair';
const GRADES: UnitGrade[] = ['like_new', 'excellent', 'good', 'fair'];
const STATUS_TONE: Record<UnitStatus, StatusTone> = { draft: 'caution', listed: 'positive', sold: 'neutral', removed: 'neutral' };
const CONDITION_TONE: Record<CrosswalkCondition, StatusTone> = { new: 'positive', open_box: 'caution', demo: 'info', display: 'neutral' };
/** The platforms a unit can be listed on (Shopify since MOT-156, BigCommerce and Lightspeed since MOT-383). */
type UnitPlatform = 'shopify' | 'bigcommerce' | 'lightspeed';
const PLATFORM_LABEL: Record<UnitPlatform, string> = { shopify: 'Shopify', bigcommerce: 'BigCommerce', lightspeed: 'Lightspeed' };
const isUnitPlatform = (value: unknown): value is UnitPlatform => value === 'shopify' || value === 'bigcommerce' || value === 'lightspeed';

interface StoreUnit {
  id: string;
  productId: string;
  mappingId: string;
  condition: CrosswalkCondition;
  integrationId: string;
  /** The integration's platform; a unit stored before MOT-383 reads as Shopify. */
  platform?: UnitPlatform;
  serial?: string | null;
  grade?: UnitGrade | null;
  ownPrice?: number | null;
  photos: string[];
  notes?: string | null;
  /** Where the unit was received (flag `unit_received_location`); absent when none was recorded. */
  locationId?: string | null;
  status: UnitStatus;
  /** What the last push wrote, on the unit's own platform (the server's read view of `shopify` / `bigcommerce` / `lightspeed`). */
  listing?: { platform: UnitPlatform; productId: string; variantId: string | null; pushedAt: string; price: number; compareAtPrice: number | null; priceSource: 'own' | 'rule' | 'new' } | null;
  /** Set when the order pull marked the unit sold; absent on a unit marked sold by hand. */
  sale?: { orderItemId: string; platformOrderId: string; orderNumber: string | null; soldAt: string | null } | null;
}

interface UnitsResponse {
  units?: StoreUnit[];
  planLocked?: boolean;
}

interface MappingOption {
  id: string;
  productId: string | null;
  storeSku?: string;
  storeBarcode?: string;
  condition: CrosswalkCondition;
  integrationId?: string | null;
  product?: { id: string; name: string; brand?: string; sku?: string };
}

interface IntegrationOption {
  id: string;
  platform?: string;
  storeName?: string;
  name?: string;
}

interface Preview {
  title: string;
  newPrice: number;
  price: number;
  compareAtPrice: number | null;
  source: 'own' | 'rule' | 'new';
  floored: boolean;
  mapPrice: number | null;
  guard: { blocked: false } | { blocked: true; code: string; message: string };
  /**
   * Lightspeed, flag `unit_received_location`: where the quantity of one is counted. `shopId` is the location's linked
   * shop (null = no link); `countedAtShopId` the shop the push will use, checked against the account's current shops;
   * `fallback` true when that is the first shop instead of the location's.
   */
  stockLocation?: StockLocation;
}

/** What the next push does with a Lightspeed unit's quantity (server: `StoreUnitStockLocation`). */
interface StockLocation {
  locationId: string;
  locationName: string | null;
  /** The location's linked Lightspeed shop; null = no link. */
  shopId: string | null;
  /** `create` a new item, `restock` a sold sibling's item, `update` the unit's own (never writes a quantity). */
  mode: 'create' | 'restock' | 'update';
  /** The shop the push writes the quantity at, checked against the account's current shops; null = none written. */
  countedAtShopId: string | null;
  /** True when the quantity does not go to the linked shop (no link, or that shop is gone). */
  fallback: boolean;
}

interface LocationOption {
  id: string;
  name: string;
  isActive?: boolean;
}

interface UnitForm {
  id?: string;
  mappingId: string;
  integrationId: string;
  serial: string;
  grade: '' | UnitGrade;
  ownPrice: string;
  photos: string[];
  notes: string;
  locationId: string;
}

const emptyForm = (): UnitForm => ({ mappingId: '', integrationId: '', serial: '', grade: '', ownPrice: '', photos: [], notes: '', locationId: '' });

const errorMessage = async (response: Response, fallback: string): Promise<string> => {
  const body = await response.json().catch(() => ({}));
  return body?.error || fallback;
};

const isHttps = (value: string): boolean => {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

const COLUMNS = [
  { key: 'product', header: 'product', width: 'minmax(0,1.6fr)' },
  { key: 'sku', header: 'sku', width: 'minmax(0,1fr)', mobileLabel: true },
  { key: 'condition', header: 'condition', width: '130px', mobileLabel: true },
  { key: 'price', header: 'price', width: '140px', mobileLabel: true },
  { key: 'status', header: 'status', width: '120px', mobileLabel: true },
  { key: 'actions', header: 'actions', width: '132px', align: 'end' as const },
];

/**
 * The confirm dialog's line on where a Lightspeed unit's quantity goes, matching what the push will do
 * (create, restock or update), so it never promises a shop the push will not write.
 */
const StockLocationLine: React.FC<{ stock: StockLocation }> = ({ stock }) => {
  const { t } = useAppTranslation();
  const location = stock.locationName || stock.locationId;
  if (stock.mode === 'update') return <Typography sx={{ color: tokens.ink.muted }}>{t('store.storeUnits.previewStockUpdate')}</Typography>;
  if (stock.countedAtShopId && !stock.fallback) {
    return <Typography>{t('store.storeUnits.previewStock', { location, shop: stock.countedAtShopId })}</Typography>;
  }
  let message: string;
  if (stock.countedAtShopId && stock.shopId) message = t('store.storeUnits.previewStockGone', { location, shop: stock.shopId });
  else if (stock.countedAtShopId) message = t('store.storeUnits.previewStockFallback', { location });
  else if (stock.mode === 'restock') message = t('store.storeUnits.previewStockRestockNotSet', { location });
  else message = t('store.storeUnits.previewStockUnknown');
  return <InlineMessage severity="info">{message}</InlineMessage>;
};

/**
 * Per-unit listings (flag `condition_pricing`): one row per physical unit the
 * store lists on its own, hanging off a crosswalk mapping (condition and SKU)
 * and a Shopify, BigCommerce or Lightspeed integration. Listing a unit writes to
 * the store, so it is confirmed first with the price the push would use, from
 * the server's own preview (whose guard also says when a platform cannot take it).
 */
export const StoreUnitsTab: React.FC = () => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const { formatCurrency } = useLocale();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<'' | UnitStatus>('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<UnitForm>(emptyForm());
  const [toDelete, setToDelete] = useState<StoreUnit | null>(null);
  const [toPush, setToPush] = useState<StoreUnit | null>(null);
  // Flag `unit_received_location` (MOT-418): where a unit was received; a Lightspeed unit's quantity is counted there.
  const { enabled: locationEnabled } = useOrgFeatureFlag('unit_received_location');

  const { data, isLoading, error, refetch } = useQuery<UnitsResponse>({
    queryKey: ['storeUnits'],
    queryFn: async () => {
      const response = await makeApiCall('/store/units', { headers: await getAuthHeaders() });
      if (!response.ok) throw new Error(await errorMessage(response, t('store.storeUnits.loadError')));
      return response.json();
    },
  });
  const units = data?.units || [];
  const planLocked = data?.planLocked === true;

  const { data: mappingsData } = useQuery<{ mappings: MappingOption[] }>({
    queryKey: ['skuCrosswalkAllForUnits'],
    queryFn: async () => {
      const response = await makeApiCall('/store/sku-crosswalk?limit=500', { headers: await getAuthHeaders() });
      if (!response.ok) return { mappings: [] };
      return response.json();
    },
  });
  // A unit needs a linked mapping with an identifier to list under; `new` units are allowed but rare.
  const mappings = useMemo(
    () => (mappingsData?.mappings || []).filter((m) => m.productId && (m.storeSku || m.storeBarcode)),
    [mappingsData],
  );
  const mappingById = useMemo(() => new Map(mappings.map((m) => [m.id, m])), [mappings]);

  const { data: integrations } = useQuery<IntegrationOption[]>({
    queryKey: ['integrations'],
    queryFn: async () => {
      const response = await makeApiCall('/integrations', { headers: await getAuthHeaders() });
      if (!response.ok) return [];
      return (await response.json()).data || [];
    },
  });
  const { data: locations } = useQuery<LocationOption[]>({
    queryKey: ['store-locations'],
    enabled: locationEnabled,
    queryFn: async () => {
      const response = await makeApiCall('/store/locations', { headers: await getAuthHeaders() });
      if (!response.ok) return [];
      return (await response.json()).data || [];
    },
  });
  const locationName = (id?: string | null): string | null => {
    if (!id) return null;
    return (locations || []).find((l) => l.id === id)?.name || null;
  };

  const unitIntegrations = useMemo(() => (integrations || []).filter((i) => !i.platform || isUnitPlatform(i.platform)), [integrations]);
  const integrationName = (id: string): string => {
    const match = (integrations || []).find((i) => i.id === id);
    return match ? match.storeName || match.name || match.platform || id : id;
  };
  /** The platform a unit lists on: its own field, else its integration's, else Shopify (slice 2). */
  const platformOf = (unit: StoreUnit): UnitPlatform => {
    if (unit.platform) return unit.platform;
    const platform = (integrations || []).find((i) => i.id === unit.integrationId)?.platform;
    return isUnitPlatform(platform) ? platform : 'shopify';
  };
  const platformLabel = (unit: StoreUnit | null): string => PLATFORM_LABEL[unit ? platformOf(unit) : 'shopify'];

  const previewQuery = useQuery<Preview>({
    queryKey: ['storeUnitPreview', toPush?.id],
    enabled: Boolean(toPush),
    queryFn: async () => {
      const response = await makeApiCall(`/store/units/${encodeURIComponent(toPush!.id)}/preview`, { headers: await getAuthHeaders() });
      if (!response.ok) throw new Error(await errorMessage(response, t('store.storeUnits.previewError')));
      return response.json();
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['storeUnits'] });
    queryClient.invalidateQueries({ queryKey: ['storeUnitPreview'] });
  };

  const saveMutation = useMutation({
    mutationFn: async (values: UnitForm) => {
      const body = {
        mappingId: values.mappingId,
        integrationId: values.integrationId,
        serial: values.serial.trim() || null,
        grade: values.grade || null,
        ownPrice: parseNumberInput(values.ownPrice),
        photos: values.photos,
        notes: values.notes.trim() || null,
        // Sent only with the flag on, so the request is exactly as before when it is off.
        ...(locationEnabled ? { locationId: values.locationId || null } : {}),
      };
      const response = await makeApiCall(values.id ? `/store/units/${encodeURIComponent(values.id)}` : '/store/units', {
        method: values.id ? 'PUT' : 'POST',
        headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error(await errorMessage(response, t('store.storeUnits.saveError')));
      return response.json();
    },
    onSuccess: () => {
      invalidate();
      setFormOpen(false);
      showToast(t('store.storeUnits.saved'), 'success');
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (unit: StoreUnit) => {
      const response = await makeApiCall(`/store/units/${encodeURIComponent(unit.id)}`, { method: 'DELETE', headers: await getAuthHeaders() });
      if (!response.ok) throw new Error(await errorMessage(response, t('store.storeUnits.deleteError')));
      return response.json() as Promise<{ archived: boolean }>;
    },
    onSuccess: (result) => {
      invalidate();
      setToDelete(null);
      showToast(result.archived ? t('store.storeUnits.deletedArchived') : t('store.storeUnits.deleted'), 'success');
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const pushMutation = useMutation({
    mutationFn: async (unit: StoreUnit) => {
      const response = await makeApiCall(`/store/units/${encodeURIComponent(unit.id)}/push`, { method: 'POST', headers: await getAuthHeaders() });
      if (!response.ok) throw new Error(await errorMessage(response, t('store.storeUnits.pushError')));
      return response.json() as Promise<{ created: boolean; warnings?: string[]; unit?: StoreUnit }>;
    },
    onSuccess: (result, unit) => {
      invalidate();
      setToPush(null);
      // A warning means the listing exists but something beside it (quantity, photos, tags) needs a look in the store.
      if (result.warnings?.length) showToast(result.warnings.join(' '), 'warning');
      else showToast(result.created ? t('store.storeUnits.listed', { platform: platformLabel(unit) }) : t('store.storeUnits.updated'), 'success');
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const statusMutation = useMutation({
    mutationFn: async ({ unit, status }: { unit: StoreUnit; status: UnitStatus }) => {
      const response = await makeApiCall(`/store/units/${encodeURIComponent(unit.id)}`, {
        method: 'PUT',
        headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) throw new Error(await errorMessage(response, t('store.storeUnits.saveError')));
      return response.json();
    },
    onSuccess: () => invalidate(),
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const openAdd = () => {
    setForm({ ...emptyForm(), integrationId: unitIntegrations.length === 1 ? unitIntegrations[0].id : '' });
    setFormOpen(true);
  };
  const openEdit = (unit: StoreUnit) => {
    setForm({
      id: unit.id,
      mappingId: unit.mappingId,
      integrationId: unit.integrationId,
      serial: unit.serial || '',
      grade: unit.grade || '',
      ownPrice: unit.ownPrice != null ? String(unit.ownPrice) : '',
      photos: unit.photos || [],
      notes: unit.notes || '',
      locationId: unit.locationId || '',
    });
    setFormOpen(true);
  };

  const mappingLabel = (m: MappingOption): string =>
    [m.product ? [m.product.brand, m.product.name].filter(Boolean).join(' ') : m.productId, m.storeSku || m.storeBarcode, t(`store.skuCrosswalk.condition.${m.condition}`)]
      .filter(Boolean)
      .join(' · ');

  const counts = useMemo(() => {
    const c: Record<UnitStatus, number> = { draft: 0, listed: 0, sold: 0, removed: 0 };
    for (const u of units) c[u.status] = (c[u.status] || 0) + 1;
    return c;
  }, [units]);
  const visible = statusFilter ? units.filter((u) => u.status === statusFilter) : units;

  // A listed Lightspeed unit's quantity sits at its received shop and an update never moves it, so the server
  // refuses a location change (409) until the unit is sold or removed; the select says so instead of failing on save.
  const editedUnit = form.id ? units.find((u) => u.id === form.id) : undefined;
  const locationLocked = Boolean(editedUnit && editedUnit.status === 'listed' && platformOf(editedUnit) === 'lightspeed');

  const formValid = Boolean(form.mappingId && form.integrationId) && form.photos.every(isHttps) && (form.ownPrice === '' || (parseNumberInput(form.ownPrice) ?? 0) > 0);
  const preview = previewQuery.data;

  const renderCell = (unit: StoreUnit, key: string): React.ReactNode => {
    const mapping = mappingById.get(unit.mappingId);
    switch (key) {
      case 'product':
        return (
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>
              {mapping?.product ? [mapping.product.brand, mapping.product.name].filter(Boolean).join(' ') : unit.productId}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }} noWrap>
              {[unit.serial ? t('store.storeUnits.serialShort', { serial: unit.serial }) : null, unit.grade ? t(`store.storeUnits.grade.${unit.grade}`) : null, integrationName(unit.integrationId), locationEnabled ? locationName(unit.locationId) : null]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          </Box>
        );
      case 'sku':
        return <Typography sx={{ fontFamily: tokens.font.mono, fontSize: 13 }}>{mapping?.storeSku || mapping?.storeBarcode || '—'}</Typography>;
      case 'condition':
        return <StatusChip size="small" tone={CONDITION_TONE[unit.condition]} label={t(`store.skuCrosswalk.condition.${unit.condition}`)} />;
      case 'price':
        return (
          <Box>
            <Typography sx={{ fontSize: 13.5, fontVariantNumeric: 'tabular-nums' }}>
              {unit.listing ? formatCurrency(unit.listing.price) : unit.ownPrice != null ? formatCurrency(unit.ownPrice) : t('store.storeUnits.priceByRule')}
            </Typography>
            {unit.listing && (
              <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>{t(`store.storeUnits.priceSource.${unit.listing.priceSource}`)}</Typography>
            )}
          </Box>
        );
      case 'status':
        return (
          <Box>
            <StatusChip size="small" dot tone={STATUS_TONE[unit.status]} label={t(`store.storeUnits.status.${unit.status}`)} />
            {unit.status === 'sold' && unit.sale && (
              <Typography sx={{ fontSize: 12, color: tokens.ink.muted, mt: 0.25 }} noWrap>
                {t('store.storeUnits.soldOnOrder', { order: unit.sale.orderNumber || unit.sale.platformOrderId })}
              </Typography>
            )}
          </Box>
        );
      case 'actions':
        return (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            {(unit.status === 'draft' || unit.status === 'listed') && (
              <Tooltip title={planLocked ? t('store.storeUnits.planLocked') : unit.status === 'listed' ? t('store.storeUnits.updateListing') : t('store.storeUnits.list', { platform: platformLabel(unit) })}>
                <span>
                  <IconButton size="small" onClick={() => setToPush(unit)} disabled={planLocked} aria-label={unit.status === 'listed' ? t('store.storeUnits.updateListing') : t('store.storeUnits.list', { platform: platformLabel(unit) })}>
                    <Storefront fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {unit.status === 'listed' && (
              <Button size="small" onClick={() => statusMutation.mutate({ unit, status: 'sold' })} disabled={planLocked || statusMutation.isPending}>
                {t('store.storeUnits.markSold')}
              </Button>
            )}
            <IconButton size="small" onClick={() => openEdit(unit)} disabled={planLocked} aria-label={t('common.edit')}>
              <Edit fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setToDelete(unit)} disabled={planLocked} aria-label={t('common.delete')}>
              <Delete fontSize="small" />
            </IconButton>
          </Stack>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <SectionCard
        dense
        title={t('store.storeUnits.title', { count: units.length })}
        subtitle={t('store.storeUnits.description')}
        actions={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as '' | UnitStatus)}
              SelectProps={{ displayEmpty: true }}
              sx={{ minWidth: 150 }}
              aria-label={t('store.storeUnits.columns.status')}
            >
              <MenuItem value="">{t('store.storeUnits.filter.all', { count: units.length })}</MenuItem>
              {(['draft', 'listed', 'sold', 'removed'] as UnitStatus[]).map((s) => (
                <MenuItem key={s} value={s}>{`${t(`store.storeUnits.status.${s}`)} (${counts[s]})`}</MenuItem>
              ))}
            </TextField>
            <Tooltip title={planLocked ? t('store.storeUnits.planLocked') : mappings.length === 0 ? t('store.storeUnits.needMapping') : ''}>
              <span>
                <Button variant="contained" size="small" startIcon={<Add />} onClick={openAdd} disabled={planLocked || mappings.length === 0}>
                  {t('store.storeUnits.add')}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        }
      >
        {planLocked && (
          <Box sx={{ p: 2 }}>
            <InlineMessage severity="info">
              {t('store.storeUnits.planLockedHelp')} <RouterLink to="/app/billing">{t('store.conditionPricing.planLockedLink')}</RouterLink>
            </InlineMessage>
          </Box>
        )}
        {error && (
          <Box sx={{ p: 2 }}>
            <InlineMessage severity="error">
              {(error as Error).message} <Button size="small" onClick={() => refetch()}>{t('store.conditionPricing.retry')}</Button>
            </InlineMessage>
          </Box>
        )}
        <ResponsiveRows
          columns={COLUMNS.map((c) => ({ ...c, header: t(`store.storeUnits.columns.${c.header}`) }))}
          rows={visible}
          isLoading={isLoading}
          getRowKey={(u) => u.id}
          renderCell={renderCell}
          emptyState={
            <EmptyState
              size="compact"
              title={statusFilter ? t('store.storeUnits.emptyFiltered', { status: t(`store.storeUnits.status.${statusFilter}`) }) : t('store.storeUnits.emptyTitle')}
              description={statusFilter ? undefined : t('store.storeUnits.emptyDescription')}
            />
          }
        />
        <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, px: 2.5, py: 1.5 }}>{t('store.storeUnits.footnote')}</Typography>
      </SectionCard>

      <FormDialog
        open={formOpen}
        title={form.id ? t('store.storeUnits.editTitle') : t('store.storeUnits.addTitle')}
        onClose={() => setFormOpen(false)}
        onSubmit={() => saveMutation.mutate(form)}
        submitLabel={form.id ? t('common.save') : t('store.storeUnits.add')}
        cancelLabel={t('common.cancel')}
        submitDisabled={!formValid}
        loading={saveMutation.isPending}
      >
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            select
            label={t('store.storeUnits.fields.mapping')}
            value={form.mappingId}
            onChange={(e) => setForm((f) => ({ ...f, mappingId: e.target.value }))}
            helperText={t('store.storeUnits.fields.mappingHelp')}
            fullWidth
          >
            {mappings.map((m) => (
              <MenuItem key={m.id} value={m.id}>{mappingLabel(m)}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('store.storeUnits.fields.integration')}
            value={form.integrationId}
            onChange={(e) => setForm((f) => ({ ...f, integrationId: e.target.value }))}
            helperText={unitIntegrations.length === 0 ? t('store.storeUnits.fields.noIntegration') : t('store.storeUnits.fields.integrationHelp')}
            fullWidth
          >
            {unitIntegrations.map((i) => (
              <MenuItem key={i.id} value={i.id}>{`${i.storeName || i.name || i.id} · ${PLATFORM_LABEL[isUnitPlatform(i.platform) ? i.platform : 'shopify']}`}</MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField label={t('store.storeUnits.fields.serial')} value={form.serial} onChange={(e) => setForm((f) => ({ ...f, serial: e.target.value }))} sx={{ flex: '1 1 200px' }} />
            <TextField select label={t('store.storeUnits.fields.grade')} value={form.grade} onChange={(e) => setForm((f) => ({ ...f, grade: e.target.value as UnitForm['grade'] }))} sx={{ flex: '1 1 160px' }} SelectProps={{ displayEmpty: true }}>
              <MenuItem value="">{t('store.storeUnits.grade.none')}</MenuItem>
              {GRADES.map((g) => (
                <MenuItem key={g} value={g}>{t(`store.storeUnits.grade.${g}`)}</MenuItem>
              ))}
            </TextField>
          </Stack>
          <NumberField
            label={t('store.storeUnits.fields.ownPrice')}
            value={form.ownPrice}
            onChange={(v) => setForm((f) => ({ ...f, ownPrice: v }))}
            min={0}
            adornment="$"
            helperText={t('store.storeUnits.fields.ownPriceHelp')}
          />
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>{t('store.storeUnits.fields.photos')}</Typography>
            <ChipInput
              value={form.photos}
              onChange={(photos) => setForm((f) => ({ ...f, photos: photos.slice(0, 10) }))}
              validate={(v) => (isHttps(v) ? null : t('store.storeUnits.fields.photosInvalid'))}
              placeholder="https://"
              aria-label={t('store.storeUnits.fields.photos')}
            />
            <Typography sx={{ fontSize: 12, color: tokens.ink.muted, mt: 0.5 }}>{t('store.storeUnits.fields.photosHelp')}</Typography>
          </Box>
          {locationEnabled && (
            <TextField
              select
              label={t('store.storeUnits.fields.location')}
              value={form.locationId}
              onChange={(e) => setForm((f) => ({ ...f, locationId: e.target.value }))}
              helperText={locationLocked ? t('store.storeUnits.fields.locationLocked') : t('store.storeUnits.fields.locationHelp')}
              disabled={locationLocked}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            >
              <MenuItem value="">{t('store.storeUnits.fields.locationNone')}</MenuItem>
              {(locations || [])
                .filter((l) => l.isActive !== false || l.id === form.locationId)
                .map((l) => (
                  <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                ))}
            </TextField>
          )}
          <TextField label={t('store.storeUnits.fields.notes')} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} multiline minRows={2} helperText={t('store.storeUnits.fields.notesHelp')} />
        </Stack>
      </FormDialog>

      <ConfirmDialog
        open={Boolean(toDelete)}
        title={t('store.storeUnits.deleteTitle')}
        message={toDelete?.listing ? t('store.storeUnits.deleteListedMessage', { platform: platformLabel(toDelete) }) : t('store.storeUnits.deleteMessage')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        confirmColor="error"
        loading={deleteMutation.isPending}
        onConfirm={() => toDelete && deleteMutation.mutate(toDelete)}
        onClose={() => setToDelete(null)}
      />

      <ConfirmDialog
        open={Boolean(toPush)}
        title={toPush?.listing ? t('store.storeUnits.pushUpdateTitle', { platform: platformLabel(toPush) }) : t('store.storeUnits.pushTitle', { platform: platformLabel(toPush) })}
        message={t('store.storeUnits.pushMessage', { integration: toPush ? integrationName(toPush.integrationId) : '' })}
        preview={
          previewQuery.isLoading ? (
            <Typography sx={{ fontSize: 13, color: tokens.ink.muted }}>{t('common.loading')}</Typography>
          ) : previewQuery.error ? (
            <InlineMessage severity="error">{(previewQuery.error as Error).message}</InlineMessage>
          ) : preview ? (
            <Stack spacing={0.75} sx={{ fontSize: 13.5 }}>
              <Typography sx={{ fontWeight: 600 }}>{preview.title}</Typography>
              <Typography>
                {t('store.storeUnits.previewPrice', { price: formatCurrency(preview.price), newPrice: formatCurrency(preview.newPrice) })}
                {' · '}
                {t(`store.storeUnits.priceSource.${preview.source}`)}
                {preview.floored ? ` · ${t('store.storeUnits.previewFloored')}` : ''}
              </Typography>
              {preview.compareAtPrice ? <Typography>{t('store.storeUnits.previewCompareAt', { price: formatCurrency(preview.compareAtPrice) })}</Typography> : null}
              {preview.mapPrice ? <Typography sx={{ color: tokens.ink.muted }}>{t('store.storeUnits.previewMap', { price: formatCurrency(preview.mapPrice) })}</Typography> : null}
              {preview.stockLocation ? <StockLocationLine stock={preview.stockLocation} /> : null}
              {preview.guard.blocked ? <InlineMessage severity="warning">{preview.guard.message}</InlineMessage> : null}
            </Stack>
          ) : null
        }
        confirmLabel={toPush?.listing ? t('store.storeUnits.updateListing') : t('store.storeUnits.list', { platform: platformLabel(toPush) })}
        cancelLabel={t('common.cancel')}
        loading={pushMutation.isPending}
        confirmDisabled={!preview || preview.guard.blocked}
        onConfirm={() => {
          if (toPush && preview && !preview.guard.blocked) pushMutation.mutate(toPush);
        }}
        onClose={() => setToPush(null)}
      />
    </>
  );
};

export default StoreUnitsTab;
